import { createContext, useContext, useState, useEffect } from "react";
import { ethers } from "ethers";
import { message } from "antd";
import BNBRide from "../artifacts/contracts/BNBRide.sol/BNBRide.json";
import Fee from "../artifacts/contracts/Fee.sol/Fee.json";

const Web3Context = createContext();

// Network Configuration
const NETWORK_CONFIG = {
  chainId: `0x${Number(process.env.REACT_APP_CHAIN_ID).toString(16)}`, // Converts decimal to hex
  chainName: process.env.REACT_APP_CHAIN_NAME,
  nativeCurrency: {
    name: process.env.REACT_APP_CHAIN_NAME === "opBNB Mainnet" ? "BNB" : "tBNB",
    symbol: process.env.REACT_APP_CHAIN_NAME === "opBNB Mainnet" ? "BNB" : "tBNB",
    decimals: 18,
  },
  rpcUrls: [process.env.REACT_APP_CHAIN_RPC],
  blockExplorerUrls: [process.env.REACT_APP_CHAIN_EXPLORER],
};

/*const NETWORK_CONFIG = {
  chainId: "0x15EB",
  chainName: "opBNB Testnet",
  nativeCurrency: {
    name: "tBNB",
    symbol: "tBNB",
    decimals: 18,
  },
  rpcUrls: ["https://opbnb-testnet-rpc.bnbchain.org"],
  blockExplorerUrls: ["https://testnet.opbnbscan.com"],
};*/


// Contract Addresses
const CONTRACT_ADDRESSES = {
  main: process.env.REACT_APP_MAIN_CONTRACT,
  fee: process.env.REACT_APP_FEE_CONTRACT,
  royalty: process.env.REACT_APP_ROYALTY_CONTRACT,
};

export function Web3Provider({ children }) {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [staticContract, setStaticContract] = useState(null);
  const [staticFeeContract, setStaticFeeContract] = useState(null);
  const [networkError, setNetworkError] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [walletAddress, setWalletAddress] = useState("");
  const [isContractReady, setIsContractReady] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);

  const { ethereum } = window;

  const switchToOpBNB = async () => {
    try {
        setIsInitializing(true);
        await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: NETWORK_CONFIG.chainId }],
        });
        return true;
    } catch (switchError) {
        if (switchError.code === 4902) {
            try {
                await window.ethereum.request({
                    method: "wallet_addEthereumChain",
                    params: [NETWORK_CONFIG],
                });
                return true;
            } catch (addError) {
                console.error("Error adding network:", addError);
                message.error(`Failed to add ${NETWORK_CONFIG.chainName}`);
                setNetworkError(true);
                return false;
            }
        }
        setNetworkError(true);
        return false;
    } finally {
        setIsInitializing(false);
    } 
  };

  const initializeContracts = async () => {
    try {
      setIsInitializing(true);
      if (!ethereum) {
        //console.error("MetaMask not found");
        message.error("Please install MetaMask");
        return;
      }
      // Log chain ID
      const chainId = await ethereum.request({ method: "eth_chainId" });
      //console.log("Current chainId:", chainId);
      if (chainId !== NETWORK_CONFIG.chainId) {
        //console.log("Wrong network, switching to opBNB Testnet...");
        const switched = await switchToOpBNB();
        if (!switched) return;
      }
      // Initialize provider and signer
      const provider = new ethers.BrowserProvider(ethereum);
      //console.log("Provider initialized");

      const signer = await provider.getSigner();
      //console.log("Signer obtained:", await signer.getAddress());
      setProvider(provider);
      setSigner(signer);
      // Initialize contracts with detailed logging
      //console.log("Initializing contracts with addresses:", CONTRACT_ADDRESSES);

      const mainContract = new ethers.Contract(
        CONTRACT_ADDRESSES.main,
        BNBRide.abi,
        provider
      );
      //console.log("Main contract initialized");
      const feeContract = new ethers.Contract(
        CONTRACT_ADDRESSES.fee,
        Fee.abi,
        provider
      );
      // console.log("Fee contract initialized");
      // Verify contract connections
      try {
        const mainContractCode = await provider.getCode(
          CONTRACT_ADDRESSES.main
        );
        const feeContractCode = await provider.getCode(CONTRACT_ADDRESSES.fee);

        /*console.log("Contract code lengths:", {
          main: mainContractCode.length,
          fee: feeContractCode.length,
        });*/
        if (mainContractCode.length <= 2 || feeContractCode.length <= 2) {
          throw new Error("Contract not deployed at specified address");
        }
      } catch (error) {
        console.error("Contract verification failed:", error);
        throw error;
      }
      setStaticContract(mainContract);
      setStaticFeeContract(feeContract);
      setIsContractReady(true);
      setNetworkError(false);
      return { provider, mainContract, feeContract };
    } catch (error) {
      console.error("Contract initialization error:", error);
      message.error("Failed to initialize contracts");
      setNetworkError(true);
      return null;
    } finally {
      setIsInitializing(false);
    }
  };

  const disconnectWallet = async () => {    
      // Clear wallet connection
      setWalletAddress(null);
      setProvider(null);
      setSigner(null);
      setStaticContract(null);
      setStaticFeeContract(null);
      setIsContractReady(false);
      setIsWalletConnected(false);
      setIsInitializing(false);
      setNetworkError(false);
  };

  const connectWallet = async () => {
    try {
      const accounts = await ethereum.request({
        method: "eth_requestAccounts",
      });
      setWalletAddress(accounts[0]);
      setIsWalletConnected(true);
      return accounts[0];
    } catch (error) {
      console.error("Wallet connection error:", error);
      message.error("Failed to connect wallet");
      return null;
    }
  };
  // Utility functions
  const convert = (val) => Number(ethers.formatEther(val));
  const formatTime = (time) => {
    const sec = Math.floor((time / 1000) % 60);
    const min = Math.floor((time / 60000) % 60);
    const hr = Math.floor((time / 3600000) % 24);
    const days = Math.floor(time / 86400000);
    return `${days.toString().padStart(2, "0")} Days, ${hr
      .toString()
      .padStart(2, "0")}:${min.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  };
  const getUTCTime = (ms) => {
    const now = new Date(ms);
    return now.toISOString().replace("T", " ").slice(0, 19) + " UTC";
  };

  // Effects
  useEffect(() => {
    initializeContracts();
    connectWallet();
  }, []);

  useEffect(() => {
    if (!ethereum) return;
    const handleChainChanged = () => {
      window.location.reload();
    };
    const handleAccountsChanged = (accounts) => setWalletAddress(accounts[0]);
    ethereum.on("chainChanged", handleChainChanged);
    ethereum.on("accountsChanged", handleAccountsChanged);
    return () => {
      ethereum.removeListener("chainChanged", handleChainChanged);
      ethereum.removeListener("accountsChanged", handleAccountsChanged);
    };
  }, [ethereum]);

  const createContract = async (address, abi, signerOrProvider) => {
    try {
      return new ethers.Contract(address, abi, signerOrProvider || provider);
    } catch (error) {
      console.error("Error creating contract instance:", error);
      throw error;
    }
  };

  const value = {
    // Contract and wallet state
    provider,
    signer,
    staticContract,
    staticFeeContract,
    walletAddress,
    isContractReady,
    isWalletConnected,
    isInitializing,
    networkError,

    //Constants
    contractAddresses: CONTRACT_ADDRESSES,

    // Functions

    connectWallet,
    disconnectWallet,
    switchToOpBNB,
    convert,
    formatTime,
    getUTCTime,
    createContract,

    // Utility getters
    getContract: () => staticContract,
    getFeeContract: () => staticFeeContract,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
}

// Custom hook for easy context usage
export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (context === undefined) {
    throw new Error("useWeb3 must be used within a Web3Provider");
  }
  return context;
};
